import { useMemo, useState } from "react";
import { useShallow } from "zustand/shallow";
import { Box, Divider, ListItemAvatar, ListItemButton, ListItemText, Typography } from "@mui/material";
import { mdiCheck } from "@mdi/js";

import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import ChipList from "components/ChipList";
import FAB from "components/FAB";
import PageLayout from "components/PageLayout";
import SearchInput from "components/SearchInput";
import useNotifier from "hooks/useNotifier";
import getNameInitials from "utils/getNameInitials";
import compareString from "utils/compareString";
import { StorageBuckets } from "api/storage";
import { FriendData } from "api/friend";
import GroupApi from "api/group";
import useBoundStore from "store";
import VirtualizedList from "components/VirtualizedList";

export interface AddGroupUsersProps {
  onAddGroupUsers: () => void;
  onBack?: () => void;
}

function AddGroupUsers(props: AddGroupUsersProps) {
  const { onAddGroupUsers, onBack } = props;
  const [selectedFriends, setSelectedFriends] = useState<FriendData[]>([]);
  const [searchedFriends, setSearchedFriends] = useState<FriendData[]>([]);
  const [searchString, setSearchString] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const { showNotification } = useNotifier();
  const selected = useBoundStore((state) => state.selected);
  const smallAvatars = useBoundStore((state) => state.miscellaneous.smallAvatars);
  const { stateFriends, groups } = useBoundStore(
    useShallow((state) => ({
      stateFriends: state.appData.friends,
      groups: state.appData.groups,
    }))
  );
  const groupApi = useMemo(() => new GroupApi(showNotification), [showNotification]);
  const group = groups.find((g) => g.id === selected.groupId);

  const friends = stateFriends
    .filter((friend) => !group?.users?.includes(friend.uid))
    .sort((a, b) => compareString(a.name ?? "", b.name ?? ""));
  const selectedFriendsUids = selectedFriends.map((friend) => friend.uid);
  const remainingUsers = friends.filter((friend) => !selectedFriendsUids.includes(friend.uid));
  const friendsToRender = searchString === "" ? remainingUsers : searchedFriends;

  /**
   * Handle search results
   *
   * @param newSearchString
   * @param newSearchedFriends
   */
  function handleSearchedResults(newSearchString: string, newSearchedFriends: FriendData[]): void {
    setSearchString(newSearchString);
    setSearchedFriends(newSearchedFriends);
  }

  /**
   * Handle select user which will be added to the group
   *
   * @param selectedFriend
   */
  function handleSelectFriend(selectedFriend: FriendData): void {
    const clonedSelectedUsers = [...selectedFriends];
    clonedSelectedUsers.push(selectedFriend);

    setSelectedFriends(clonedSelectedUsers);
    setSearchString("");
  }

  /**
   * Remove selected user from added list
   *
   * @param removedSelectedUser
   */
  function handleRemoveSelectedFriend(removedSelectedUser: FriendData) {
    const clonedRemainingUsers = [...remainingUsers];
    clonedRemainingUsers.push(removedSelectedUser);

    const clonedSelectedUsers = [...selectedFriends].filter((friend) => friend.uid !== removedSelectedUser.uid);
    setSelectedFriends(clonedSelectedUsers);
  }

  /**
   * Add new friends to the group
   */
  async function handleAddFriends(): Promise<void> {
    if (group) {
      setIsAdding(true);

      const newFriends = [...group.users, ...selectedFriendsUids];
      const { error } = await groupApi.addOrRemoveGroupUsers(group.id, newFriends);

      setIsAdding(false);

      if (!error) {
        onAddGroupUsers();
      }
    }
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];

    if (selectedFriends.length) {
      vrList.push(
        <Box maxHeight='30vh' overflow='auto' px={2} mb={2}>
          <ChipList chips={selectedFriends} onDelete={handleRemoveSelectedFriend} />
        </Box>
      );
    }

    vrList.push(
      <SearchInput
        data={remainingUsers}
        onSearchedResults={handleSearchedResults}
        inputProps={{
          "aria-label": "Search users",
          placeholder: "Search users",
        }}
        searchBy='name'
        searchString={searchString}
      />
    );
    vrList.push(<Divider />);

    friendsToRender.forEach((friend) => {
      vrList.push(
        <ListItemButton key={friend.uid} onClick={() => handleSelectFriend(friend)}>
          <ListItemAvatar>
            <AsyncAvatar
              avatarFile={friend.avatar ? smallAvatars[friend.avatar] : undefined}
              avatarName={friend.avatar}
              placeholder={getNameInitials(friend.name)}
              bucket={StorageBuckets.PROFILES_AVATARS}
            />
          </ListItemAvatar>
          <ListItemText primary={friend.name} secondary={friend.tag} />
        </ListItemButton>
      );
    });

    if (!friendsToRender?.length && searchString === "") {
      vrList.push(
        <Box p={2}>
          <Typography align='center' color='textSecondary'>
            Everyone from your friends&#39; list is already part of this group.
          </Typography>
        </Box>
      );
    }

    if (!searchedFriends.length && searchString !== "") {
      vrList.push(
        <Box p={2}>
          <Typography align='center' color='textSecondary'>
            No friends were found with that name
          </Typography>
        </Box>
      );
    }

    return vrList;
  }

  return (
    <PageLayout
      appBar={<AppBar title='Add participants' onBack={onBack} />}
      fab={
        selectedFriends.length > 0 && (
          <FAB
            loading={isAdding}
            aria-label='Add participants'
            icon={mdiCheck}
            title='Check'
            onClick={handleAddFriends}
          />
        )
      }
      paddingBottom={false}
    >
      <VirtualizedList estimateSize={56} rows={generateVirtualizedList()} />
    </PageLayout>
  );
}

export default AddGroupUsers;
